import { inject, Injectable } from '@angular/core';

import {
  Action,
  Actions,
  NgxsOnInit,
  ofActionSuccessful,
  Selector,
  State,
  StateContext,
  StateToken,
  Store
} from '@ngxs/store';
import { tap } from 'rxjs';

import { MyWorldHttpService } from '@app/core/services/http/my-world-http.service';
import { ToastService } from '@app/core/ui/components/toast';
import { catchRemoteError } from '@app/shared/rxjs/custom-operators';

import {
  SetCustomerUILoadedAction,
  SetCustomerUIErrorAction,
  SetCustomerUILoadingAction
} from './customer-ui.actions';
import { CustomerUIState } from './customer-ui.state';
import {
  ClearCustomerAction,
  RegisterCustomerAction,
  ValidateCustomerAction
} from './customer.actions';
import { CustomerModel } from './customer.model';
import {
  CustomerRequestKey,
  CustomerRequestKeys
} from './customer.request-keys';
import { StudentState } from '../../../features/student/store/student/student.state';
import { RemoveStudentSessionAction } from '../../../features/student/store/student-session/student-session.action';

export interface CustomerStateModel {
  customer: CustomerModel | null;
}

const CUSTOMER_STATE_TOKEN = new StateToken<CustomerStateModel>('customer');

// keys moved to customer.request-keys.ts

@State<CustomerStateModel>({
  name: CUSTOMER_STATE_TOKEN,
  defaults: {
    customer: null
  }
})
@Injectable()
export class CustomerState implements NgxsOnInit {
  private readonly http = inject(MyWorldHttpService);
  private readonly toast = inject(ToastService);
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);

  @Selector()
  static customer({ customer }: CustomerStateModel) {
    return customer ?? null;
  }

  @Selector()
  static email(state: CustomerStateModel) {
    return state.customer?.email || '';
  }

  static requestStatusByKey(key: CustomerRequestKey) {
    return CustomerUIState.requestStatusByKey(key);
  }

  static isLoadingByKey(key: CustomerRequestKey) {
    return CustomerUIState.isLoadingByKey(key);
  }

  static isLoadedByKey(key: CustomerRequestKey) {
    return CustomerUIState.isLoadedByKey(key);
  }

  static isErrorByKey(key: CustomerRequestKey) {
    return CustomerUIState.isErrorByKey(key);
  }

  ngxsOnInit(ctx: StateContext<CustomerStateModel>): void {
    this.actions$
      .pipe(
        ofActionSuccessful(RemoveStudentSessionAction),
        tap(() => {
          ctx.dispatch(new ClearCustomerAction());
        })
      )
      .subscribe(() => {
        console.info('Student session removed, clearing customer state ✅');
      });
  }

  @Action(ClearCustomerAction)
  clearCustomer(ctx: StateContext<CustomerStateModel>) {
    ctx.patchState({
      customer: null
    });
  }

  @Action(ValidateCustomerAction)
  validateCustomerEmail(
    ctx: StateContext<CustomerStateModel>,
    { email }: ValidateCustomerAction
  ) {
    void this.store.dispatch(
      new SetCustomerUILoadingAction(CustomerRequestKeys.ValidateCustomer)
    );

    return this.http.validateCustomerEmail(email).pipe(
      catchRemoteError(
        (remoteError) => {
          this.toast.danger(remoteError.message);
          ctx.patchState({ customer: null });
          void this.store.dispatch(
            new SetCustomerUIErrorAction(CustomerRequestKeys.ValidateCustomer)
          );
        },
        {
          defaultMessage: 'Não foi possível validar seu email. Tente novamente'
        }
      ),
      tap((data) => {
        ctx.patchState({
          customer: data ?? { email }
        });
        void this.store.dispatch(
          new SetCustomerUILoadedAction(CustomerRequestKeys.ValidateCustomer)
        );
      })
    );
  }

  @Action(RegisterCustomerAction)
  registerCustomer(
    ctx: StateContext<CustomerStateModel>,
    { payload }: RegisterCustomerAction
  ) {
    const student = this.store.selectSnapshot(StudentState.student);

    const customer: CustomerModel = {
      id: payload.id,
      email: payload.email,
      phone: payload.phone,
      bondChild: payload.bondChild,
      studentId: student?.id || null
    };

    void this.store.dispatch(
      new SetCustomerUILoadingAction(CustomerRequestKeys.RegisterCustomer)
    );

    return this.http.registerCustomer(customer).pipe(
      catchRemoteError(
        (remoteError) => {
          this.toast.danger(remoteError.message);
          ctx.patchState({ customer: null });
          void this.store.dispatch(
            new SetCustomerUIErrorAction(CustomerRequestKeys.RegisterCustomer)
          );
        },
        {
          defaultMessage:
            'Não foi possível realizar o seu registro. Tente novamente'
        }
      ),
      tap((data) => {
        ctx.patchState({ customer: data });
        void this.store.dispatch(
          new SetCustomerUILoadedAction(CustomerRequestKeys.RegisterCustomer)
        );
      })
    );
  }
}
